import React, {useState, useEffect, useMemo} from 'react'
import {Box, Flex, useBreakpointValue, useMultiStyleConfig} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {renderLayoutsSwitch} from '../../utils/contentRenderer'
import {resolveGDIStyles, splitGDIResponsiveStyles, mergeGDISx, GDI_DESKTOP_MEDIA_QUERY} from '../../gdi/utils/gdi-styles'

/**
 * Optimizely field → Chakra prop mapping for GDIContentPageStylesPerViewType.
 * resolveGDIStyles applies the first entry as the default across all breakpoints;
 * subsequent entries override only the breakpoints listed in ViewTypesToApply.
 */
const PAGE_STYLE_FIELD_MAP = {
    Margins: 'padding',
    BackgroundColor: 'backgroundColor',
    PageWidth: 'maxWidth'
}

const FLUID_PAGE_WIDTHS = new Set(['fluid-container', 'full-bleed'])

/* Maps CMS column-width tokens to concrete CSS max-width values.
 * Mirrors the SECTION_WIDTH_TOKEN_MAP used in gdiCarouselGridBlock. */
const PAGE_WIDTH_TOKEN_MAP = {
    'max-width-6-cols-desktop': '50%',
    'max-width-8-cols-desktop': '924px'
}

const resolvePageWidthValue = (value) => PAGE_WIDTH_TOKEN_MAP[value] ?? value

// ─── Anchor navigation helpers ───────────────────────────────────────────────

/**
 * Returns the anchor ID for a given expanded CMS block, or null.
 * Each block type exposes the anchor via its own CMS field name.
 * Extend this function when new block types gain anchor support.
 */
const getAnchorId = (expanded) => {
    if (!expanded) return null
    if (expanded.__typename === 'GDITextBlock') return expanded.TextAnchorId || null
    return null
}

/** Strips HTML tags and decodes HTML entities, returning plain text. */
const stripHtml = (html) => {
    const stripped = (html || '').replace(/<[^>]+>/g, '').trim()
    return stripped
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, ' ')
}

/**
 * Returns a human-readable navigation label for a content block.
 * Falls back to the anchor ID when no text content is available.
 */
const getAnchorLabel = (expanded) => {
    if (!expanded) return ''
    if (expanded.__typename === 'GDITextBlock') {
        return stripHtml(expanded.TextForWeb || expanded.TextForApp) || getAnchorId(expanded) || ''
    }
    return getAnchorId(expanded) || ''
}

/**
 * Partitions a content-area array into:
 *   preItems — items before the first anchor-ID component (always visible)
 *   sections — [{id, label, items}] one entry per anchor-ID component,
 *              containing that component plus every subsequent non-anchor item
 *              up to (but not including) the next anchor-ID component.
 */
const buildSections = (items) => {
    const sections = []
    const preItems = []
    let current = null

    for (const item of items) {
        const expanded = item?.ContentLink?.Expanded
        const anchorId = getAnchorId(expanded)
        if (anchorId) {
            if (current) sections.push(current)
            current = {id: anchorId, label: getAnchorLabel(expanded), items: [item]}
        } else if (current) {
            current.items.push(item)
        } else {
            preItems.push(item)
        }
    }
    if (current) sections.push(current)

    return {preItems, sections}
}

// ─── Desktop sidebar ──────────────────────────────────────────────────────────

const AnchorSidebar = ({sections, activeId, onNavigate, styles}) => (
    <Box as="nav" __css={styles.sidebar}>
        {sections.map((section) => {
            const isActive = activeId === section.id
            return (
                <Box
                    key={section.id}
                    as="a"
                    href={`#${section.id}`}
                    __css={styles.sidebarItem}
                    data-active={isActive}
                    onClick={(e) => {
                        e.preventDefault()
                        onNavigate(section.id)
                    }}
                >
                    {section.label.toLowerCase()}
                </Box>
            )
        })}
    </Box>
)

AnchorSidebar.propTypes = {
    sections: PropTypes.arrayOf(
        PropTypes.shape({id: PropTypes.string, label: PropTypes.string})
    ).isRequired,
    activeId: PropTypes.string.isRequired,
    onNavigate: PropTypes.func.isRequired,
    styles: PropTypes.object.isRequired
}

// ─── Mobile tab bar ───────────────────────────────────────────────────────────

const AnchorTabs = ({sections, activeId, onNavigate, styles, ...rest}) => (
    <Box as="nav" __css={styles.tabs} {...rest}>
        {sections.map((section) => {
            const isActive = activeId === section.id
            return (
                <Box
                    key={section.id}
                    as="a"
                    href={`#${section.id}`}
                    __css={styles.tabItem}
                    data-active={isActive}
                    onClick={(e) => {
                        e.preventDefault()
                        onNavigate(section.id)
                    }}
                >
                    {section.label.toLowerCase()}
                </Box>
            )
        })}
    </Box>
)

AnchorTabs.propTypes = {
    sections: PropTypes.arrayOf(
        PropTypes.shape({id: PropTypes.string, label: PropTypes.string})
    ).isRequired,
    activeId: PropTypes.string.isRequired,
    onNavigate: PropTypes.func.isRequired,
    styles: PropTypes.object.isRequired
}

// ─── GDIContentPage ───────────────────────────────────────────────────────────

/**
 * GDIContentPage
 *
 * Full-bleed page template whose entire body is populated by content tiles of
 * type "layout".  Intended for: Homepage, CLP, and Content Pages.
 *
 * Supports anchor-based navigation driven by AnchorsMenuShowPosition:
 *   0 — no navigation rendered
 *   N — navigation inserted at position N in the content flow (1-based)
 *
 * Desktop: sticky left sidebar with anchor links + IntersectionObserver
 *          to highlight the active section while scrolling.
 * Mobile:  horizontally-scrollable tab bar; active tab renders only its section.
 *
 * CMS shape (Optimizely):
 *   content.ContentArea              — layout blocks
 *   content.AnchorsMenuShowPosition  — nav insertion point (Int)
 *   content.GDIContentPageMarginPerDevice — optional per-device margin overrides
 *   content.GDIContentPageStylesPerViewType — page-level style tokens
 */
const GDIContentPage = ({content}) => {
    const contentArea = content?.ContentArea
    const marginPerDevice = content?.GDIContentPageMarginPerDevice
    const stylesPerViewType = content?.GDIContentPageStylesPerViewType
    const anchorsMenuShowPosition = content?.AnchorsMenuShowPosition ?? 0

    // undefined during SSR — treated as falsy → desktop branch renders server-side
    const isMobile = useBreakpointValue({base: true, lg: false})
    const navStyles = useMultiStyleConfig('GDIContentPage', {})

    const {styles: resolvedStyles} = resolveGDIStyles(stylesPerViewType, PAGE_STYLE_FIELD_MAP)
    const {maxWidth: resolvedMaxWidth, ...remainingStyles} = resolvedStyles
    const {directStyles, sxStyles: remainingSx} = splitGDIResponsiveStyles(remainingStyles)

    // Resolve the desktop-only maxWidth to a concrete CSS value (e.g. token → '50%').
    // PageWidth is a desktop-only CMS field; base/md are intentionally unconstrained.
    const desktopMaxWidthValue = (() => {
        if (!resolvedMaxWidth) return null
        const raw = typeof resolvedMaxWidth === 'object' ? resolvedMaxWidth.lg : resolvedMaxWidth
        if (!raw || FLUID_PAGE_WIDTHS.has(raw)) return null
        return resolvePageWidthValue(raw)
    })()

    const hasMarginPerDevice =
        marginPerDevice?.MarginMobile ||
        marginPerDevice?.MarginTablet ||
        marginPerDevice?.MarginDesktop

    // Derive the navigation data structure once per content/position change.
    const {aboveNavItems, preItems, sections} = useMemo(() => {
        const items = contentArea ?? []
        if (anchorsMenuShowPosition <= 0) {
            return {aboveNavItems: items, preItems: [], sections: []}
        }
        const insertIdx = Math.max(0, anchorsMenuShowPosition - 1)
        const above = items.slice(0, insertIdx)
        const navArea = items.slice(insertIdx)
        const {preItems: pre, sections: secs} = buildSections(navArea)
        return {aboveNavItems: above, preItems: pre, sections: secs}
    }, [contentArea, anchorsMenuShowPosition])

    const hasNavigation = sections.length > 0

    const [activeId, setActiveId] = useState(() => sections[0]?.id ?? '')
    const [isScrolling, setIsScrolling] = useState(false)

    // Keep activeId valid when sections change after hydration.
    useEffect(() => {
        if (sections.length > 0 && !sections.find((s) => s.id === activeId)) {
            setActiveId(sections[0].id)
        }
    }, [sections]) // eslint-disable-line react-hooks/exhaustive-deps

    // Highlight the section currently visible in the viewport (desktop + mobile).
    useEffect(() => {
        if (!hasNavigation || isMobile === undefined) return undefined

        const observer = new IntersectionObserver(
            (entries) => {
                if (isScrolling) return
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        setActiveId(entry.target.id)
                        break
                    }
                }
            },
            {rootMargin: '-80px 0px -66% 0px', threshold: 0}
        )

        sections.forEach(({id}) => {
            const el = document.getElementById(id)
            if (el) observer.observe(el)
        })

        return () => observer.disconnect()
    }, [sections, hasNavigation, isMobile, isScrolling])

    const handleNavigate = (id) => {
        setActiveId(id)
        setIsScrolling(true)
        const el = document.getElementById(id)
        if (el) {
            const y = el.getBoundingClientRect().top + window.pageYOffset - 80
            window.scrollTo({top: y, behavior: 'smooth'})
            setTimeout(() => setIsScrolling(false), 1000)
        }
    }

    const renderItem = (item, idx) => (
        <React.Fragment key={`${item?.ContentLink?.Id ?? 'item'}-${idx}`}>
            {renderLayoutsSwitch(item?.ContentLink?.Expanded)}
        </React.Fragment>
    )

    // Width constraint via CSS media query (consistent with other GDI components).
    // For pages without sidebar navigation: constrain + centre the outer box.
    // For pages with sidebar navigation: constrain only the content column so the
    // sidebar width is additive (i.e. PageWidth targets the reading area, not the total).
    const outerWidthSx = !hasNavigation && desktopMaxWidthValue
        ? {[GDI_DESKTOP_MEDIA_QUERY]: {maxWidth: desktopMaxWidthValue, marginLeft: 'auto', marginRight: 'auto'}}
        : {}
    const contentWidthSx = hasNavigation && desktopMaxWidthValue
        ? {[GDI_DESKTOP_MEDIA_QUERY]: {maxWidth: desktopMaxWidthValue}}
        : {}

    const outerBoxProps = {
        ...directStyles,
        sx: mergeGDISx(remainingSx, outerWidthSx),
        ...(hasMarginPerDevice && {
            margin: {
                base: marginPerDevice.MarginMobile || undefined,
                md: marginPerDevice.MarginTablet || undefined,
                lg: marginPerDevice.MarginDesktop || undefined
            }
        }),
        'data-block-type': 'GDIContentPage'
    }

    // ── No anchor navigation: original render path ──
    if (!hasNavigation) {
        return (
            <Box {...outerBoxProps}>
                {(contentArea ?? []).map(renderItem)}
            </Box>
        )
    }

    return (
        <Box {...outerBoxProps}>
            {isMobile ? (
                // ── Mobile: anchor tab bar + full content ──
                <Box>
                    {aboveNavItems.map(renderItem)}
                    {preItems.map(renderItem)}
                    <AnchorTabs
                        sections={sections}
                        activeId={activeId}
                        onNavigate={handleNavigate}
                        styles={navStyles}
                        {...(remainingStyles?.padding?.base && {
                            mx: `-${remainingStyles.padding.base.split(' ')[1] ?? remainingStyles.padding.base}`,
                            px: remainingStyles.padding.base.split(' ')[1] ?? remainingStyles.padding.base
                        })}
                    />
                    {sections.flatMap((section) => section.items).map(renderItem)}
                </Box>
            ) : (
                // ── Desktop (+ SSR default): sticky sidebar + full scrollable content ──
                <Flex alignItems="flex-start">
                    <AnchorSidebar
                        sections={sections}
                        activeId={activeId}
                        onNavigate={handleNavigate}
                        styles={navStyles}
                    />
                    <Box
                        alignSelf="stretch"
                        flexShrink={0}
                        width="1px"
                        backgroundColor="webTheme.strokeTertiary"
                        mx="64px"
                    />
                    <Box flex="1" sx={contentWidthSx}>
                        {aboveNavItems.map(renderItem)}
                        {preItems.map(renderItem)}
                        {sections.flatMap((section) => section.items).map(renderItem)}
                    </Box>
                </Flex>
            )}
        </Box>
    )
}

GDIContentPage.displayName = 'GDIContentPage'

GDIContentPage.propTypes = {
    content: PropTypes.shape({
        ContentArea: PropTypes.arrayOf(
            PropTypes.shape({
                ContentLink: PropTypes.shape({
                    Id: PropTypes.number,
                    Expanded: PropTypes.object
                })
            })
        ),
        AnchorsMenuShowPosition: PropTypes.number,
        GDIContentPageMarginPerDevice: PropTypes.shape({
            MarginMobile: PropTypes.string,
            MarginTablet: PropTypes.string,
            MarginDesktop: PropTypes.string
        }),
        GDIContentPageStylesPerViewType: PropTypes.array
    })
}

export default GDIContentPage
